import ptBR from './locales/pt-BR.json';
import enUS from './locales/en-US.json';
import esES from './locales/es-ES.json';
import frFR from './locales/fr-FR.json';
import deDE from './locales/de-DE.json';

// Configuração dos idiomas suportados
export const languages = {
  'pt-BR': {
    name: 'Português (Brasil)',
    flag: 'br',
    translations: ptBR
  },
  'en-US': {
    name: 'English (US)',
    flag: 'us',
    translations: enUS
  },
  'es-ES': {
    name: 'Español (España)',
    flag: 'es',
    translations: esES
  },
  'fr-FR': {
    name: 'Français (France)',
    flag: 'fr',
    translations: frFR
  },
  'de-DE': {
    name: 'Deutsch (Deutschland)',
    flag: 'de',
    translations: deDE
  }
};

// Idioma padrão
export const defaultLanguage = 'pt-BR';

// Função para obter tradução
export const getTranslation = (language = defaultLanguage) => {
  const lang = languages[language];
  if (!lang) {
    console.warn(`Language ${language} not found, using default`);
    return languages[defaultLanguage].translations;
  }
  
  return lang.translations;
};

// Função para obter texto traduzido por chave
export const t = (key, language = defaultLanguage) => {
  const translations = getTranslation(language);
  const keys = key.split('.');
  let value = translations;
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      console.warn(`Translation key "${key}" not found for language ${language}`);
      return key; // Retorna a chave se não encontrar a tradução
    }
  }
  
  return value;
};

// Função para obter lista de idiomas disponíveis
export const getAvailableLanguages = () => {
  return Object.keys(languages).map(code => ({
    code,
    name: languages[code].name,
    flag: languages[code].flag
  }));
};

// Detectar país do usuário
export const detectUserCountry = async () => {
  try {
    // Tentar geolocalização primeiro
    if (navigator.geolocation) {
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const response = await fetch(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
              );
              const data = await response.json();
              const countryCode = data.countryCode?.toLowerCase();
              resolve(countryCode);
            } catch (error) {
              console.warn('Erro na geolocalização:', error);
              resolve(null);
            }
          },
          async () => {
            // Fallback para IP
            try {
              const response = await fetch('https://ipapi.co/json/');
              const data = await response.json();
              resolve(data.country_code?.toLowerCase());
            } catch (error) {
              console.warn('Erro na detecção por IP:', error);
              resolve(null);
            }
          },
          { timeout: 5000 }
        );
      });
    } else {
      // Fallback direto para IP
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        return data.country_code?.toLowerCase();
      } catch (error) {
        console.warn('Erro na detecção por IP:', error);
        return null;
      }
    }
  } catch (error) {
    console.warn('Erro na detecção de país:', error);
    return null;
  }
};

// Detectar idioma baseado no país
export const detectLanguageByCountry = async () => {
  const country = await detectUserCountry();
  
  // Mapear países para idiomas
  const countryLanguageMap = {
    'br': 'pt-BR',  // Brasil
    'us': 'en-US',  // Estados Unidos
    'es': 'es-ES',  // Espanha
    'mx': 'es-ES',  // México
    'ar': 'es-ES',  // Argentina
    'co': 'es-ES',  // Colômbia
    'pe': 'es-ES',  // Peru
    'cl': 'es-ES',  // Chile
    've': 'es-ES',  // Venezuela
    'ec': 'es-ES',  // Equador
    'bo': 'es-ES',  // Bolívia
    'py': 'es-ES',  // Paraguai
    'uy': 'es-ES',  // Uruguai
    'cr': 'es-ES',  // Costa Rica
    'pa': 'es-ES',  // Panamá
    'gt': 'es-ES',  // Guatemala
    'hn': 'es-ES',  // Honduras
    'sv': 'es-ES',  // El Salvador
    'ni': 'es-ES',  // Nicarágua
    'do': 'es-ES',  // República Dominicana
    'cu': 'es-ES',  // Cuba
    'pr': 'es-ES',  // Porto Rico
    'ca': 'en-US',  // Canadá
    'gb': 'en-US',  // Reino Unido
    'au': 'en-US',  // Austrália
    'nz': 'en-US',  // Nova Zelândia
    'ie': 'en-US',  // Irlanda
    'za': 'en-US',  // África do Sul
    'in': 'en-US',  // Índia
    'sg': 'en-US',  // Singapura
    'hk': 'en-US',  // Hong Kong
    'my': 'en-US',  // Malásia
    'ph': 'en-US',  // Filipinas
    'th': 'en-US',  // Tailândia
    'id': 'en-US',  // Indonésia
    'vn': 'en-US',  // Vietnã
    'kr': 'en-US',  // Coreia do Sul
    'jp': 'en-US',  // Japão
    'cn': 'en-US',  // China
    'tw': 'en-US',  // Taiwan
    'mo': 'en-US',  // Macau
    'pt': 'pt-BR',  // Portugal
    'ao': 'pt-BR',  // Angola
    'mz': 'pt-BR',  // Moçambique
    'cv': 'pt-BR',  // Cabo Verde
    'gw': 'pt-BR',  // Guiné-Bissau
    'st': 'pt-BR',  // São Tomé e Príncipe
    'tl': 'pt-BR',  // Timor-Leste
    'gq': 'pt-BR',  // Guiné Equatorial
    'fr': 'fr-FR',  // França
    'be': 'fr-FR',  // Bélgica (francês)
    'ch': 'fr-FR',  // Suíça (francês)
    'lu': 'fr-FR',  // Luxemburgo
    'mc': 'fr-FR',  // Mônaco
    'sn': 'fr-FR',  // Senegal
    'ci': 'fr-FR',  // Costa do Marfim
    'ml': 'fr-FR',  // Mali
    'bf': 'fr-FR',  // Burkina Faso
    'ne': 'fr-FR',  // Níger
    'td': 'fr-FR',  // Chade
    'mg': 'fr-FR',  // Madagascar
    'cm': 'fr-FR',  // Camarões
    'cd': 'fr-FR',  // República Democrática do Congo
    'cg': 'fr-FR',  // República do Congo
    'ga': 'fr-FR',  // Gabão
    'cf': 'fr-FR',  // República Centro-Africana
    'dj': 'fr-FR',  // Djibuti
    'km': 'fr-FR',  // Comores
    'sc': 'fr-FR',  // Seychelles
    'vu': 'fr-FR',  // Vanuatu
    'nc': 'fr-FR',  // Nova Caledônia
    'pf': 'fr-FR',  // Polinésia Francesa
    'wf': 'fr-FR',  // Wallis e Futuna
    'de': 'de-DE',  // Alemanha
    'at': 'de-DE',  // Áustria
    'li': 'de-DE',  // Liechtenstein
    'ch': 'de-DE',  // Suíça (alemão)
  };
  
  if (country && countryLanguageMap[country]) {
    return countryLanguageMap[country];
  }
  
  // Fallback para detecção do navegador
  return detectBrowserLanguage();
};

// Função para detectar idioma do navegador (fallback)
export const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.languages?.[0] || 'pt-BR';
  
  // Mapear idiomas do navegador para nossos códigos
  if (browserLang.startsWith('en')) return 'en-US';
  if (browserLang.startsWith('es')) return 'es-ES';
  if (browserLang.startsWith('pt')) return 'pt-BR';
  if (browserLang.startsWith('fr')) return 'fr-FR';
  if (browserLang.startsWith('de')) return 'de-DE';
  
  return defaultLanguage;
};
